import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { observer } from 'mobx-react-lite';

import { apiClient } from '@api';

interface ErrorResponse {
  statusCode: number;
  error: string;
  description?: string;
}

const testBadRequestError = async () => {
  const { data } = await apiClient.post<ErrorResponse>('api/auth/login', {
    email: '', // Missing email will trigger BadRequestError
    password: '',
  });
  return data;
};

const testValidationError = async () => {
  const { data } = await apiClient.post<ErrorResponse>('api/users', {
    // Missing required fields will trigger validation error
    email: 'invalid-email', // Invalid email format
  });
  return data;
};

const testNotFoundError = async () => {
  const userUUID = '123e4567-e89b-12d3-a456-426614174000';
  const { data } = await apiClient.get<ErrorResponse>(`api/users/${userUUID}`);
  return data;
};

const testUnauthorizedError = async () => {
  const { data } = await apiClient.get<ErrorResponse>('api/test/unauthorized');
  return data;
};

const testServerError = async () => {
  const { data } = await apiClient.get<ErrorResponse>('api/test/server-error');
  return data;
};

const testCustomBadRequest = async () => {
  const { data } = await apiClient.get<ErrorResponse>('api/test/bad-request');
  return data;
};

export const ErrorTestComponent = observer(() => {
  const [results, setResults] = useState<Array<{ test: string; result: string; error?: string }>>(
    [],
  );

  const addResult = (test: string, result: string, error?: string) => {
    setResults((prev) => [...prev, { test, result, error }]);
  };

  const clearResults = () => {
    setResults([]);
  };

  // Bad Request Error Test
  const badRequestMutation = useMutation({
    mutationFn: testBadRequestError,
    onSuccess: (data) => {
      addResult('Bad Request Error', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Bad Request Error', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  // Validation Error Test
  const validationMutation = useMutation({
    mutationFn: testValidationError,
    onSuccess: (data) => {
      addResult('Validation Error', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Validation Error', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  // Not Found Error Test
  const notFoundMutation = useMutation({
    mutationFn: testNotFoundError,
    onSuccess: (data) => {
      addResult('Not Found Error', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Not Found Error', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  // Unauthorized Error Test
  const unauthorizedMutation = useMutation({
    mutationFn: testUnauthorizedError,
    onSuccess: (data) => {
      addResult('Unauthorized Error', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Unauthorized Error', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  // Server Error Test
  const serverErrorMutation = useMutation({
    mutationFn: testServerError,
    onSuccess: (data) => {
      addResult('Server Error', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Server Error', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  // Custom Bad Request Test
  const customBadRequestMutation = useMutation({
    mutationFn: testCustomBadRequest,
    onSuccess: (data) => {
      addResult('Custom Bad Request', 'Success', JSON.stringify(data, null, 2));
    },
    onError: (error: AxiosError) => {
      addResult('Custom Bad Request', 'Error', JSON.stringify(error.response?.data, null, 2));
    },
  });

  return (
    <div
      style={{
        padding: '20px',
        border: '2px solid #e74c3c',
        borderRadius: '8px',
        margin: '20px 0',
      }}
    >
      <div style={{ marginBottom: '20px' }}>
        <p style={{ color: '#666', fontSize: '14px' }}>
          This component tests different types of errors to demonstrate the global exception filter.
          Each button will trigger a different type of error and show the standardized error
          response format.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '10px',
          marginBottom: '20px',
        }}
      >
        <button
          onClick={() => badRequestMutation.mutate()}
          disabled={badRequestMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: badRequestMutation.isPending ? 0.6 : 1,
          }}
        >
          {badRequestMutation.isPending ? 'Testing...' : 'Test Bad Request (400)'}
        </button>

        <button
          onClick={() => validationMutation.mutate()}
          disabled={validationMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#f39c12',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: validationMutation.isPending ? 0.6 : 1,
          }}
        >
          {validationMutation.isPending ? 'Testing...' : 'Test Validation Error (400)'}
        </button>

        <button
          onClick={() => notFoundMutation.mutate()}
          disabled={notFoundMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#9b59b6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: notFoundMutation.isPending ? 0.6 : 1,
          }}
        >
          {notFoundMutation.isPending ? 'Testing...' : 'Test Not Found (404)'}
        </button>

        <button
          onClick={() => unauthorizedMutation.mutate()}
          disabled={unauthorizedMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#e67e22',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: unauthorizedMutation.isPending ? 0.6 : 1,
          }}
        >
          {unauthorizedMutation.isPending ? 'Testing...' : 'Test Unauthorized (401)'}
        </button>

        <button
          onClick={() => serverErrorMutation.mutate()}
          disabled={serverErrorMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#c0392b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: serverErrorMutation.isPending ? 0.6 : 1,
          }}
        >
          {serverErrorMutation.isPending ? 'Testing...' : 'Test Server Error (500)'}
        </button>

        <button
          onClick={() => customBadRequestMutation.mutate()}
          disabled={customBadRequestMutation.isPending}
          style={{
            padding: '10px',
            backgroundColor: '#8e44ad',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            opacity: customBadRequestMutation.isPending ? 0.6 : 1,
          }}
        >
          {customBadRequestMutation.isPending ? 'Testing...' : 'Test Custom Bad Request (400)'}
        </button>

        <button
          onClick={clearResults}
          style={{
            padding: '10px',
            backgroundColor: '#34495e',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Clear Results
        </button>
      </div>

      {results.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '10px' }}>Test Results:</h3>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {results.map((result, index) => (
              <div
                key={index}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '15px',
                  marginBottom: '10px',
                  backgroundColor: result.error ? '#fdf2f2' : '#f0f9ff',
                }}
              >
                <h4 style={{ margin: '0 0 10px 0', color: result.error ? '#dc2626' : '#059669' }}>
                  {result.test} - {result.result}
                </h4>
                {result.error && (
                  <pre
                    style={{
                      backgroundColor: '#1f2937',
                      color: '#f9fafb',
                      padding: '10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {result.error}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});
